import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Briefcase, Calendar, MessageSquare } from "lucide-react";
import ajithImage from "../../assets/Profiles/ajith-kumar.png";
import meghanaImage from "../../assets/Profiles/meghana.png";
import praveenImage from "../../assets/Profiles/praveen.png";
import deepaImage from "../../assets/Profiles/ananya.png";
import rajeshImage from "../../assets/Profiles/rajesh.png";
// 6th testimonial image – replace with your actual asset
import sunitaImage from "../../assets/Profiles/deepika.png";

const Testimony = () => {
  const testimonials = [
    {
      id: 1,
      name: "Ajith Kumar",
      role: "Software Engineer",
      testimonial:
        "I needed a dermatologist nearby with weekend availability. Dr. Kavya was available on Saturday at 5:30 PM. Booked in less than 2 minutes. Super convenient experience!",
      rating: 4.4,
      date: "March 2026",
      image: ajithImage,
    },
    {
      id: 2,
      name: "Meghana R",
      role: "HR Manager",
      testimonial:
        "I kept delaying my health checkup because of work. LIVMO helped me consult online and schedule a clinic visit after office hours. Finally got everything done without disrupting my workday.",
      rating: 4.7,
      date: "February 2026",
      image: meghanaImage,
    },
    {
      id: 3,
      name: "Praveen J",
      role: "Operations Director",
      testimonial:
        "Our company started using LIVMO for employee consultations. Appointments, prescriptions, and follow-ups are all managed in one place. The entire process feels much more organized now.",
      rating: 5.0,
      date: "February 2026",
      image: praveenImage,
    },
    {
      id: 4,
      name: "Deepika S",
      role: "Product Manager",
      testimonial:
        "The home care service arranged by LIVMO was exceptional. My mother recovered comfortably at home with regular physiotherapy sessions. Everything was coordinated seamlessly.",
      rating: 4.7,
      date: "January 2026",
      image: deepaImage,
    },
    {
      id: 5,
      name: "Rajesh K",
      role: "Finance Director",
      testimonial:
        "Finding a good diagnostic lab that accepts corporate insurance used to be a headache. LIVMO sorted it in one call. Reports came directly to my email within 24 hours.",
      rating: 4.6,
      date: "January 2026",
      image: rajeshImage,
    },
    {
      id: 6,
      name: "Ananya R",
      role: "Wellness Coordinator",
      testimonial:
        "LIVMO's platform made it easy to book therapy sessions for our entire team. The coordination team handled everything - from scheduling to follow-ups. Truly a lifesaver!",
      rating: 4.9,
      date: "December 2025",
      image: sunitaImage,
    },
  ];

  // Group testimonials into slides (2 per slide for desktop, 1 per slide for mobile)
  // We'll handle this dynamically in the JSX based on screen size.
  const [slideIndex, setSlideIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [cardsPerView, setCardsPerView] = useState(1);

  // Update cardsPerView on resize
  useEffect(() => {
    const updateCardsPerView = () => {
      if (window.innerWidth >= 1024) {
        setCardsPerView(2); // desktop
      } else {
        setCardsPerView(1); // tablet & mobile
      }
    };
    updateCardsPerView();
    window.addEventListener("resize", updateCardsPerView);
    return () => window.removeEventListener("resize", updateCardsPerView);
  }, []);

  const totalSlides = Math.ceil(testimonials.length / cardsPerView);

  // Auto-slide every 8 seconds (slower for reading)
  useEffect(() => {
    const interval = setInterval(() => {
      setDirection(1);
      setSlideIndex((prev) => (prev + 1) % totalSlides);
    }, 8000);
    return () => clearInterval(interval);
  }, [totalSlides]);

  // Get current slide's testimonials
  const currentSlideTestimonials = testimonials.slice(
    slideIndex * cardsPerView,
    slideIndex * cardsPerView + cardsPerView
  );

  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return (
      <div className="flex items-center gap-0.5 sm:gap-1">
        {[...Array(fullStars)].map((_, i) => (
          <Star
            key={`full-${i}`}
            size={14}
            className="fill-amber-400 text-amber-400 sm:size-[16px]"
          />
        ))}
        {hasHalfStar && (
          <div className="relative">
            <Star size={14} className="text-amber-400 sm:size-[16px]" />
            <div
              className="absolute top-0 left-0 overflow-hidden"
              style={{ width: "50%" }}
            >
              <Star
                size={14}
                className="fill-amber-400 text-amber-400 sm:size-[16px]"
              />
            </div>
          </div>
        )}
        {[...Array(emptyStars)].map((_, i) => (
          <Star
            key={`empty-${i}`}
            size={14}
            className="text-gray-300 sm:size-[16px]"
          />
        ))}
        <span className="ml-1 text-xs sm:text-sm font-semibold text-gray-700">
          {rating.toFixed(1)}
        </span>
      </div>
    );
  };

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 200, damping: 25, duration: 0.5 },
    },
    exit: (direction) => ({
      x: direction > 0 ? -300 : 300,
      opacity: 0,
      transition: { duration: 0.4 },
    }),
  };

  return (
    <section className="bg-gradient-to-b from-secondary/30 to-white py-12 md:py-16 lg:py-20 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-5 md:px-8">
        {/* Header */}
        <div className="text-center mb-10 md:mb-14">
          <div className="flex justify-center mb-6 md:mb-8 lg:mb-10">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-royalBlue/10 to-royalBlue/5 backdrop-blur-sm rounded-full px-4 py-1.5 sm:px-5 md:px-7 sm:py-2 md:py-3 border border-royalBlue/20 shadow-lg">
              <MessageSquare size={16} className="text-royalGreen sm:size-[18px]" />
              <p className="font-poppins font-semibold text-royalBlue text-[11px] xs:text-sm sm:text-base md:text-lg tracking-wide uppercase whitespace-nowrap">
                TESTIMONIALS
              </p>
            </div>
          </div>
          <h2 className="text-darkblue font-poppins font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl mt-2 px-4">
            Trusted by <span className="text-royalBlue">Corporate Employees</span>
          </h2>
          <p className="text-text1/80 font-poppins text-sm sm:text-base max-w-2xl mx-auto mt-3 px-4">
            See why professionals across India choose LIVMO for their healthcare needs
          </p>
        </div>

        {/* Carousel Container */}
        <div className="relative">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={slideIndex}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              className={`grid gap-6 lg:gap-8 ${
                cardsPerView === 2 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1"
              }`}
            >
              {currentSlideTestimonials.map((testimonial) => (
                <div
                  key={testimonial.id}
                  className="bg-white rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300 h-full"
                >
                  <div className="p-5 sm:p-6 md:p-7 flex flex-col h-full">
                    {/* Profile Row */}
                    <div className="flex items-start gap-3 sm:gap-4 mb-5">
                      <div className="relative flex-shrink-0">
                        <img
                          src={testimonial.image}
                          alt={testimonial.name}
                          className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover border-2 border-royalBlue/20 shadow-md"
                        />
                        <div className="absolute -bottom-0.5 -right-0.5 bg-green-500 rounded-full border-2 border-white">
                          <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full"></div>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-poppins font-bold text-darkblue text-base sm:text-lg truncate">
                          {testimonial.name}
                        </h3>
                        <div className="flex items-center gap-1 text-gray-600 text-xs sm:text-sm mt-0.5">
                          <Briefcase size={12} className="flex-shrink-0 sm:size-[14px]" />
                          <span className="font-medium truncate">{testimonial.role}</span>
                        </div>
                      </div>
                    </div>

                    {/* Rating & Date */}
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                      {renderStars(testimonial.rating)}
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <Calendar size={12} />
                        <span>{testimonial.date}</span>
                      </div>
                    </div>

                    {/* Testimonial Text */}
                    <div className="relative flex-grow">
                      <div className="absolute -top-2 -left-2 text-royalBlue/10 hidden xs:block">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M10 11h-4v-4h4v4zm8 0h-4v-4h4v4zm0 8h-4v-4h4v4zm-8 0h-4v-4h4v4z" />
                        </svg>
                      </div>
                      <p className="text-gray-700 font-poppins leading-relaxed text-sm sm:text-base pl-0 xs:pl-5 break-words whitespace-normal">
                        “{testimonial.testimonial}”
                      </p>
                    </div>

                    {/* Verified Badge */}
                    <div className="mt-6 pt-4 border-t border-gray-100">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 bg-royalBlue rounded-full"></div>
                          <span className="text-[11px] xs:text-xs text-gray-500 font-medium">
                            Verified Corporate Employee
                          </span>
                        </div>
                        {/* <div className="flex items-center gap-1">
                          <img
                            src="https://upload.wikimedia.org/wikipedia/commons/c/ca/LinkedIn_logo_initials.png"
                            alt="LinkedIn"
                            className="w-3 h-3 xs:w-4 xs:h-4 opacity-50"
                          />
                          <span className="text-[10px] xs:text-xs text-gray-400">
                            Posted as recommendation
                          </span>
                        </div> */}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </motion.div>
          </AnimatePresence>

          {/* Progress Indicators (no interaction, just visual) */}
          <div className="flex justify-center mt-8 gap-2">
            {Array.from({ length: totalSlides }).map((_, idx) => (
              <div
                key={idx}
                className={`h-1.5 rounded-full transition-all duration-500 ${
                  idx === slideIndex ? "w-6 bg-royalBlue" : "w-1.5 bg-gray-300"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimony;